/**
 * Spatius Backend Mode 客户端，协议与同级目录 spatious-web-vue-demo 一致。
 *
 * 后端持有 Motion Server；前端只渲染推过来的音频和动作帧：
 *   avatar_audio  → controller.yieldAudioData
 *   avatar_frames → controller.yieldFramesData
 *
 * 课件对口型走 { type: 'audio_url', url }，由服务端拉音频并驱动形象。
 */

const DEFAULT_WS_PATH = '/ws/spatius/agent'

function spatiousBackendWsUrl() {
  if (typeof process !== 'undefined' && process.env && process.env.VUE_APP_SPATIUS_WS_URL) {
    return process.env.VUE_APP_SPATIUS_WS_URL
  }
  const protocol = (typeof location !== 'undefined' && location.protocol === 'https:') ? 'wss:' : 'ws:'
  return protocol + '//readapi.bluebirdabc.com' + DEFAULT_WS_PATH
}

function base64ToBytes(base64) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

export class SpatiusBackendClient {
  constructor(callbacks) {
    this.callbacks = callbacks || {}
    this.socket = null
    this.controller = null
    this.conversationId = null
  }

  get isOpen() {
    return !!(this.socket && this.socket.readyState === WebSocket.OPEN)
  }

  setController(controller) {
    this.controller = controller || null
  }

  connect() {
    if (this.isOpen) return Promise.resolve()
    const url = spatiousBackendWsUrl()
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(url)
      this.socket = socket
      const timer = window.setTimeout(() => {
        reject(new Error('连接 Spatius 后端超时: ' + url))
      }, 15000)

      socket.onmessage = (event) => {
        let msg
        try {
          msg = JSON.parse(event.data)
        } catch (e) {
          return
        }
        switch (msg.type) {
          case 'ready':
            window.clearTimeout(timer)
            if (this.callbacks.onReady) this.callbacks.onReady(msg.avatar || {})
            resolve()
            break
          case 'avatar_audio': {
            const controller = this.controller
            if (!controller) break
            const bytes = msg.audio ? base64ToBytes(msg.audio) : new Uint8Array(0)
            this.conversationId =
              controller.yieldAudioData(bytes, msg.isLast || false) || this.conversationId
            break
          }
          case 'avatar_frames': {
            const controller = this.controller
            if (!controller || !this.conversationId) break
            const frames = (msg.frames || []).map((f) => base64ToBytes(f))
            if (frames.length) controller.yieldFramesData(frames, this.conversationId)
            if (msg.isLast) this.conversationId = null
            break
          }
          case 'interrupt':
            if (this.controller && this.controller.interrupt) this.controller.interrupt()
            this.conversationId = null
            break
          case 'error':
            if (this.callbacks.onError) this.callbacks.onError(msg.message || 'Spatius 后端错误')
            break
          default:
            break
        }
      }

      socket.onerror = () => {
        window.clearTimeout(timer)
        reject(new Error('无法连接 Spatius 后端: ' + url))
      }

      socket.onclose = () => {
        this.conversationId = null
        if (this.callbacks.onClosed) this.callbacks.onClosed()
      }
    })
  }

  send(payload) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload))
    }
  }

  setAvatar(avatarId) {
    this.send({ type: 'set_avatar', avatarId })
  }

  playAudioUrl(url) {
    this.send({ type: 'audio_url', url })
  }

  interrupt() {
    if (this.controller && this.controller.interrupt) this.controller.interrupt()
    this.conversationId = null
    this.send({ type: 'interrupt' })
  }

  close() {
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
    this.conversationId = null
    this.controller = null
  }
}
